/**
 * Who may do what on this board, as this page sees it (`access.*`).
 *
 * An open core (the server's own board, the desktop app, the page's own core) makes everyone
 * an owner. A space's members are its team's accounts, each with their team role: viewer,
 * commenter, editor or owner. The core checks every request against the asker's role; this
 * service only mirrors the answer of `access.state` (asked again on every connection and every
 * `access.changed`) so the GUI can say what you are and hide what you may not do. Who is in a
 * team is managed through the accounts (`accounts.ts`).
 */
import { createSignal, type Accessor } from "solid-js";
import type { Access } from "@protocol/Access";
import type { Role } from "@protocol/Role";
import type { Client } from "../client/client";

/** Every role, from least to most: each can do what the ones before it can. */
export const ROLES: readonly Role[] = ["viewer", "commenter", "editor", "owner"];

/** What the {@link AccessService} offers. */
export interface AccessService {
  /** The last answer of `access.state` (`null` before the first one). */
  state: Accessor<Access | null>;
  /** This page's role (`null`: not known yet, or not a member). */
  role(): Role | null;
  /**
   * Whether this page may do what needs `role`. While the role is not known yet it says yes:
   * the core still checks every request.
   */
  can(role: Role): boolean;
  /** Whether this board has members (a space), rather than being open to everyone. */
  shared(): boolean;
  /** Asks the core again. */
  refresh(): Promise<void>;
}

/** Whether `role` is at least `needed`. */
export function atLeast(role: Role, needed: Role): boolean {
  return ROLES.indexOf(role) >= ROLES.indexOf(needed);
}

/** Starts following who may do what. Call inside a Solid root. */
export function createAccess(client: Client): AccessService {
  const [state, setState] = createSignal<Access | null>(null);

  /** Reads `access.state` (a core without the module leaves it open). */
  async function refresh(): Promise<void> {
    try {
      const answer = await client.request({ method: "access.state" });
      if (answer.type === "access") setState(answer.access);
    } catch {
      // An older core, or the connection dropped: the next connection asks again.
    }
  }

  client.onOpen(() => void refresh());
  client.onEvent((event) => {
    if (event.event === "access.changed") void refresh();
  });

  const role = () => state()?.role ?? null;
  return {
    state,
    role,
    can(needed) {
      const current = state();
      if (!current) return true;
      return current.role !== null && atLeast(current.role, needed);
    },
    shared: () => state()?.open === false,
    refresh,
  };
}
